import { useLayoutEffect, useCallback, useState, useEffect, } from "react";
import { IToolbarButtonActiveContentType } from "../types";
export function getRect(element) {
    if (!element) {
        return {
            bottom: 0,
            height: 0,
            left: 0,
            right: 0,
            top: 0,
            width: 0,
        };
    }
    return element.getBoundingClientRect();
}
/**
 * Hook to get the bounding client rect of a rendered element.
 */
export var useRect = function (ref) {
    var _a = useState(getRect(ref ? ref.current : null)), rect = _a[0], setRect = _a[1];
    var handleResize = useCallback(function () {
        if (!ref.current) {
            return;
        }
        // Update client rect
        setRect(getRect(ref.current));
    }, [ref]);
    useLayoutEffect(function () {
        var element = ref.current;
        if (!element) {
            return;
        }
        handleResize();
        if (typeof ResizeObserver === "function") {
            var resizeObserver_1 = new ResizeObserver(function () { return handleResize(); });
            resizeObserver_1.observe(element);
            return function () {
                resizeObserver_1.disconnect();
            };
        }
        else {
            // Older browser support
            window.addEventListener("resize", handleResize);
            return function () {
                window.removeEventListener("resize", handleResize);
            };
        }
    }, [ref, handleResize]);
    return rect;
};
/**
 * This hook contains the logic to handle focus and keyboard arrow navigation inside a menu component
 * After toggling the control, the focus is set on the first element of the menu and a keydown listener is attached to the list items
 * The activeIndex state variable keeps track of which menu item is currently active / focused
 * The keydown listener updates state index if one of these keys are pressed : ArrowUp, ArrowDown, and Escape
 */
export var useKeyboardDropDownNavigation = function (_a) {
    var isExpanded = _a.isExpanded, setIsExpanded = _a.setIsExpanded, activeStateContentType = _a.activeStateContentType;
    var _b = useState(null), menuReferenceElement = _b[0], setMenuReferenceElement = _b[1];
    var _c = useState(-1), activeIndex = _c[0], setActiveIndex = _c[1];
    var onKeyDown = function (event) {
        if (activeStateContentType !== IToolbarButtonActiveContentType.DROPDOWN) {
            return;
        }
        if (event.key === "ArrowDown" &&
            (menuReferenceElement === null || menuReferenceElement === void 0 ? void 0 : menuReferenceElement.children) &&
            activeIndex < (menuReferenceElement === null || menuReferenceElement === void 0 ? void 0 : menuReferenceElement.children.length) - 1) {
            setActiveIndex(function (prevState) { return prevState + 1; });
        }
        else if (event.key === "ArrowUp" && activeIndex > 0) {
            setActiveIndex(function (prevState) { return prevState - 1; });
        }
        else if (event.key === "Escape") {
            setIsExpanded(false);
            setActiveIndex(-1);
        }
    };
    useEffect(function () {
        if (!isExpanded) {
            setActiveIndex(-1);
        }
        else {
            setActiveIndex(0);
        }
    }, [isExpanded]);
    useEffect(function () {
        var _a;
        if (!menuReferenceElement ||
            activeIndex < 0 ||
            activeIndex >= menuReferenceElement.children.length) {
            return;
        }
        var menuItems = Array.from(menuReferenceElement.children);
        if (menuItems[activeIndex]) {
            (_a = menuItems[activeIndex].querySelector("button")) === null || _a === void 0 ? void 0 : _a.focus();
        }
    }, [activeIndex, menuReferenceElement]);
    return {
        menuReferenceElement: menuReferenceElement,
        setMenuReferenceElement: setMenuReferenceElement,
        onKeyDown: onKeyDown,
    };
};
